import React from 'react';
import { useBffClient } from '../api/client';
import { ApiError } from '../api/client';
import { useUpsellReporter } from './use-upsell';
import type { UpsellSurface } from '../plan/free-tier';

export type UpgradeState = 'idle' | 'upgrading' | 'done' | 'conflict' | 'error';

/**
 * Upgrades a free personal organization to a team (story #136).
 *
 * The call is NOT quick. On the server it quiesces the organization, provisions a database, copies
 * every row, verifies the counts and only then cuts over, so this can take a few seconds and the
 * caller has to show that rather than look broken.
 *
 * On success the page is RELOADED rather than patched. Almost everything the shell decided - which nav
 * items exist, which routes resolve, what the device cap is - was decided from `isPersonal`, and
 * reloading re-derives all of it from the server in one step instead of leaving the app to unpick which
 * pieces of cached state are now wrong.
 */
export function useUpgradeToTeam(surface?: UpsellSurface) {
  const client = useBffClient();
  const report = useUpsellReporter();
  const [state, setState] = React.useState<UpgradeState>('idle');

  const upgrade = React.useCallback(async () => {
    if (surface) report(surface, true);

    setState('upgrading');
    try {
      await client.post('/bff/organizations/current/upgrade');
      setState('done');
      window.location.reload();
    } catch (err) {
      // 409 means an upgrade is already running - a double-clicked button, most likely. That is not a
      // failure and must not be shown as one.
      setState(err instanceof ApiError && err.status === 409 ? 'conflict' : 'error');
    }
  }, [client, report, surface]);

  return { state, upgrade };
}
